import React, { useEffect, useState } from "react";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import KpiStatCard from "../components/KpiStatCard";
import AssetCard from "../components/AssetCard";
import AssetTableView from "../components/AssetTableView";
import QuickReadingModal from "../components/QuickReadingModal";
import QuickStatusModal from "../components/QuickStatusModal";
import QuickServiceModal from "../components/QuickServiceModal";
import EditMachineryModal from "../components/EditMachineryModal";
import TransferMachineryModal from "../components/TransferMachineryModal";
import AssetDetailDrawer from "../components/AssetDetailDrawer";
import SiteSearchSelector from "../components/SiteSearchSelector";
import { safeText } from "../utils/hubHelpers";

export default function FleetRegister({ onOpenAddMachine }) {
  const [loading, setLoading] = useState(false);
  const [machineryList, setMachineryList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  // Filter states
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedFuel, setSelectedFuel] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'

  // Modal / Drawer states
  const [activeMachine, setActiveMachine] = useState(null);
  const [isReadingModalOpen, setIsReadingModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  useEffect(() => {
    fetchSites();
    fetchDashboardMetrics();
    fetchMachinery();
  }, [selectedSiteId, selectedStatus, selectedFuel]);

  const fetchSites = async () => {
    try {
      const res = await callApi(apiUrl.get_site, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.site)
          ? res.data.site
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setSitesList(list);
      }
    } catch (e) {
      console.error("Error fetching sites:", e);
    }
  };

  const fetchDashboardMetrics = async () => {
    try {
      const res = await callApi(apiUrl.get_maintenance_dashboard, { method: "GET" });
      if (res?.status === true) {
        setDashboardData(res?.data);
      }
    } catch (e) {
      console.error("Error fetching maintenance dashboard:", e);
    }
  };

  const fetchMachinery = async () => {
    setLoading(true);
    try {
      let queryParams = `?pageSize=100`;
      if (selectedSiteId) queryParams += `&siteId=${selectedSiteId}`;
      if (searchQuery) queryParams += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await callApi(`${apiUrl.get_machinery}${queryParams}`, { method: "GET" });
      if (res?.status === true) {
        const rawList = Array.isArray(res?.data?.machine)
          ? res.data.machine
          : Array.isArray(res?.data?.machinery)
          ? res.data.machinery
          : Array.isArray(res?.data?.rows)
          ? res.data.rows
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setMachineryList(rawList);
      } else {
        setMachineryList([]);
      }
    } catch (e) {
      console.error("Error fetching machinery:", e);
      setMachineryList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchMachinery();
    }
  };

  // Safe list
  const safeList = Array.isArray(machineryList) ? machineryList : [];
  const safeSites = Array.isArray(sitesList) ? sitesList : [];

  // Client-side filtering for fast interactive feel
  const filteredMachinery = safeList.filter((m) => {
    if (!m) return false;
    if (selectedStatus && m.status !== selectedStatus) return false;
    if (selectedFuel && m.fuelType !== selectedFuel) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = m.name?.toLowerCase().includes(q);
      const matchReg = m.machineNumber?.toLowerCase().includes(q);
      const matchAssetNo = m.assetNo?.toLowerCase().includes(q);
      const matchModel = m.model?.toLowerCase().includes(q);
      if (!matchName && !matchReg && !matchAssetNo && !matchModel) return false;
    }
    return true;
  });

  // Calculate live KPI metrics
  const totalFleetCount = safeList.length;
  const operationalCount = safeList.filter((m) => !m.status || m.status === "Operational").length;
  const downCount = safeList.filter((m) => m.status === "Breakdown" || m.status === "Under Service").length;
  const totalBookValue = safeList.reduce((sum, item) => sum + (Number(item?.purchaseCost) || 0), 0);
  const totalBookValueLakhs = (totalBookValue / 100000).toFixed(2);

  return (
    <div>
      {/* 1. Top KPI Stat Grid */}
      <div className="kpi-grid">
        <KpiStatCard
          label="Total Fleet Assets"
          value={totalFleetCount}
          subtext={`Across ${safeSites.length || 0} solar & project sites`}
          icon={<span>🚜</span>}
          color="#3b82f6"
          iconBg="#eff6ff"
        />

        <KpiStatCard
          label="Operational & Ready"
          value={operationalCount}
          subtext={`${totalFleetCount > 0 ? Math.round((operationalCount / totalFleetCount) * 100) : 0}% fleet health`}
          icon={<span>🟢</span>}
          color="#10b981"
          iconBg="#ecfdf5"
        />

        <KpiStatCard
          label="Down / In Service"
          value={downCount}
          subtext="Breakdown or maintenance active"
          icon={<span>⚠️</span>}
          color="#ef4444"
          iconBg="#fef2f2"
        />

        <KpiStatCard
          label="Total Fleet Valuation"
          value={`₹${totalBookValueLakhs} L`}
          subtext="Book value at purchase"
          icon={<span>💼</span>}
          color="#8b5cf6"
          iconBg="#f5f3ff"
        />
      </div>

      {/* 2. Searchable Site Selector */}
      {(() => {
        const siteCountsMap = safeSites.reduce((acc, site) => {
          const sId = site?.id || site?.value || "";
          acc[sId] = safeList.filter((m) => m && (m.siteId === sId || m.Site?.id === sId)).length;
          return acc;
        }, {});

        return (
          <SiteSearchSelector
            sites={safeSites}
            selectedSiteId={selectedSiteId}
            onSelectSite={setSelectedSiteId}
            countsMap={siteCountsMap}
            totalCount={safeList.length}
          />
        );
      })()}

      {/* 3. Filter Toolbar */}
      <div className="hub-toolbar">
        <div className="toolbar-left">
          <div className="search-box-wrap">
            <span className="search-icon-pos">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search machine name, reg no, asset ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <select
            className="filter-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Operational">Operational</option>
            <option value="Breakdown">Breakdown</option>
            <option value="Under Service">Under Service</option>
            <option value="Idle">Idle</option>
          </select>

          <select
            className="filter-select"
            value={selectedFuel}
            onChange={(e) => setSelectedFuel(e.target.value)}
          >
            <option value="">All Fuel Types</option>
            <option value="Diesel">Diesel</option>
            <option value="Petrol">Petrol</option>
            <option value="Electric">Electric</option>
            <option value="CNG">CNG</option>
          </select>
        </div>

        <div className="toolbar-right">
          <div className="view-switcher-group">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
            >
              <span>🔲</span> Cards
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              <span>📋</span> Table
            </button>
          </div>

          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setIsTransferModalOpen(true)}
            style={{
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            🔁 Transfer Machine
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={onOpenAddMachine}
            style={{
              background: "#059669",
              borderColor: "#059669",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: "12.5px",
              height: 34,
              padding: "0 13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span>+</span> Add Machine
          </button>
        </div>
      </div>

      {/* 4. Machinery View: Cards Grid or Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status" style={{ marginBottom: "1rem" }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Loading Machinery Fleet...</p>
        </div>
      ) : filteredMachinery.length === 0 ? (
        <div
          style={{
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: 12,
            padding: "4rem 2rem",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "2.5rem", display: "block", marginBottom: "0.5rem" }}>🚜</span>
          <h4 style={{ color: "#0f172a", fontWeight: 700 }}>No Equipment Found</h4>
          <p style={{ color: "#64748b", maxWidth: 420, margin: "0 auto 1.5rem auto", fontSize: "0.875rem" }}>
            No machinery matches your current search or filter criteria. Try resetting filters or register new equipment.
          </p>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              setSelectedSiteId("");
              setSelectedStatus("");
              setSelectedFuel("");
              setSearchQuery("");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="asset-cards-grid">
          {filteredMachinery.map((item) => (
            <AssetCard
              key={item.id}
              item={item}
              onInspect={(m) => {
                setActiveMachine(m);
                setIsDetailDrawerOpen(true);
              }}
              onLogReading={(m) => {
                setActiveMachine(m);
                setIsReadingModalOpen(true);
              }}
              onChangeStatus={(m) => {
                setActiveMachine(m);
                setIsStatusModalOpen(true);
              }}
              onAddService={(m) => {
                setActiveMachine(m);
                setIsServiceModalOpen(true);
              }}
              onEdit={(m) => {
                setActiveMachine(m);
                setIsEditModalOpen(true);
              }}
              onTransfer={(m) => {
                setActiveMachine(m);
                setIsTransferModalOpen(true);
              }}
            />
          ))}
        </div>
      ) : (
        <AssetTableView
          items={filteredMachinery}
          onInspect={(m) => {
            setActiveMachine(m);
            setIsDetailDrawerOpen(true);
          }}
          onLogReading={(m) => {
            setActiveMachine(m);
            setIsReadingModalOpen(true);
          }}
          onChangeStatus={(m) => {
            setActiveMachine(m);
            setIsStatusModalOpen(true);
          }}
          onAddService={(m) => {
            setActiveMachine(m);
            setIsServiceModalOpen(true);
          }}
          onEdit={(m) => {
            setActiveMachine(m);
            setIsEditModalOpen(true);
          }}
          onTransfer={(m) => {
            setActiveMachine(m);
            setIsTransferModalOpen(true);
          }}
        />
      )}

      {/* Action Modals */}
      <QuickReadingModal
        isOpen={isReadingModalOpen}
        toggle={() => setIsReadingModalOpen(!isReadingModalOpen)}
        machine={activeMachine}
        onSuccess={fetchMachinery}
      />

      <QuickStatusModal
        isOpen={isStatusModalOpen}
        toggle={() => setIsStatusModalOpen(!isStatusModalOpen)}
        machine={activeMachine}
        onSuccess={() => {
          fetchMachinery();
          fetchDashboardMetrics();
        }}
      />

      <QuickServiceModal
        isOpen={isServiceModalOpen}
        toggle={() => setIsServiceModalOpen(!isServiceModalOpen)}
        machine={activeMachine}
        onSuccess={() => {
          fetchMachinery();
          fetchDashboardMetrics();
        }}
      />

      {/* Edit Machine Modal */}
      <EditMachineryModal
        isOpen={isEditModalOpen}
        toggle={() => setIsEditModalOpen(!isEditModalOpen)}
        machine={activeMachine}
        onSuccess={() => {
          fetchMachinery();
          fetchDashboardMetrics();
        }}
      />

      {/* Transfer Machinery Modal */}
      <TransferMachineryModal
        isOpen={isTransferModalOpen}
        toggle={() => setIsTransferModalOpen(!isTransferModalOpen)}
        machine={activeMachine}
        onSuccess={() => {
          fetchMachinery();
          fetchDashboardMetrics();
        }}
      />

      {/* 360 Detail Drawer */}
      <AssetDetailDrawer
        isOpen={isDetailDrawerOpen}
        onClose={() => setIsDetailDrawerOpen(false)}
        machineryId={activeMachine?.id}
        onRefreshList={fetchMachinery}
        onEdit={(m) => {
          setActiveMachine(m);
          setIsEditModalOpen(true);
        }}
        onTransfer={(m) => {
          setActiveMachine(m);
          setIsTransferModalOpen(true);
        }}
      />
    </div>
  );
}

